/**
 * PageSetupConverter - Bidirectional conversion between database and WASM page setup formats
 * 
 * PURPOSE:
 * - Converts human-readable database format (inches) to WASM native format (26.6 fixed-point)
 * - Converts WASM format back to database format for UI display
 * - Eliminates hardcoded magic numbers in rendering pipeline
 * - Ensures document-aware layout that respects saved page setup metadata
 * 
 * ARCHITECTURE:
 * Database (AST metadata) → PageSetupConverter.toWasmFormat() → WASM Module
 * WASM Module → PageSetupConverter.fromWasmFormat() → UI Display
 * 
 * CONVERSION FORMULAS:
 * - Inches → CSS Pixels: pixels = inches × 96 (W3C CSS spec)
 * - CSS Pixels → Fixed-Point: fixedPoint = pixels × 64 (26.6 format)
 * - Combined: fixedPoint = inches × 96 × 64 = inches × 6144
 * - Content Width: pageWidth - leftMargin - rightMargin
 * - Content Height: pageHeight - topMargin - bottomMargin
 * 
 * @see EdgeDocs_Text_Rendering_Architecture.md for detailed documentation
 * @see scripts/CONVERSION_ANALYSIS.md for mathematical derivation
 */

import { FixedPoint, PageSetup, PAGE_SIZES } from '../core/types/Typesetter';
import { ProtocolInitializeDocumentPageSetup } from '../core/types/ReactiveMessages';

/**
 * Database page setup format (stored in AST metadata)
 * Human-readable format with inches for all measurements
 */
export interface DatabasePageSetup {
  paperSize: {
    name: string;        // "Letter (8.5\" x 11\")", "A4 (8.27\" x 11.69\")", etc.
    width: number;       // Width in inches (e.g., 8.5)
    height: number;      // Height in inches (e.g., 11)
  };
  orientation: 'portrait' | 'landscape';
  margins: {
    top: number;         // Top margin in inches (e.g., 1.0)
    right: number;       // Right margin in inches (e.g., 1.0)
    bottom: number;      // Bottom margin in inches (e.g., 1.0)
    left: number;        // Left margin in inches (e.g., 1.0)
  };
  pageColor?: string;    // Hex color (e.g., "#ffffff")
  wordSpacingPercent?: number; // Word spacing adjustment
}

/**
 * Validation error thrown when page setup format is invalid
 */
export class PageSetupValidationError extends Error {
  constructor(message: string, public field?: string, public value?: any) {
    super(message);
    this.name = 'PageSetupValidationError';
  }
}

// Re-export protocol type for convenience in converter usage
export type InitializeDocumentPageSetup = ProtocolInitializeDocumentPageSetup;

/**
 * PageSetupConverter - Unified conversion utility for all page setup formats
 *
 * Consolidates conversion logic between:
 * - Database format (human-readable inches)
 * - WASM format (26.6 fixed-point)
 * - InitializeDocument format (flat structure with mixed units)
 */
export class PageSetupConverter {
  /**
   * Convert database page setup (inches) to WASM PageSetup (fixed-point)
   * 
   * @param dbPageSetup - Database page setup from AST metadata
   * @returns WASM-compatible PageSetup with 26.6 fixed-point coordinates
   * @throws PageSetupValidationError if input is invalid
   * 
   * @example
   * const dbSetup = {
   *   paperSize: { name: "Letter", width: 8.5, height: 11 },
   *   orientation: "portrait",
   *   margins: { top: 1, right: 1, bottom: 1, left: 1 }
   * };
   * const wasmSetup = PageSetupConverter.toWasmFormat(dbSetup);
   * // Result: { page_size: "Letter", margins_fp: { top: 6144, ... }, ... }
   */
  static toWasmFormat(dbPageSetup: Partial<DatabasePageSetup> | null | undefined): PageSetup {
    // Validate input
    if (!dbPageSetup) {
      console.warn('[PageSetupConverter] No page setup provided, using default Letter');
      return PAGE_SIZES.LETTER;
    }

    try {
      // Extract paper dimensions with defaults
      const widthInches = dbPageSetup.paperSize?.width ?? 8.5;
      const heightInches = dbPageSetup.paperSize?.height ?? 11;
      
      // Validate paper dimensions
      if (widthInches <= 0 || widthInches > 100) {
        throw new PageSetupValidationError(
          `Invalid paper width: ${widthInches} inches (must be 0-100)`,
          'paperSize.width',
          widthInches
        );
      }
      if (heightInches <= 0 || heightInches > 100) {
        throw new PageSetupValidationError(
          `Invalid paper height: ${heightInches} inches (must be 0-100)`,
          'paperSize.height',
          heightInches
        );
      }

      // Extract margins with defaults
      const margins = dbPageSetup.margins ?? { top: 1, right: 1, bottom: 1, left: 1 };
      const marginTop = margins.top ?? 1;
      const marginRight = margins.right ?? 1;
      const marginBottom = margins.bottom ?? 1;
      const marginLeft = margins.left ?? 1;

      // Validate margins
      if (marginTop < 0 || marginTop > 10) {
        throw new PageSetupValidationError(
          `Invalid top margin: ${marginTop} inches (must be 0-10)`,
          'margins.top',
          marginTop
        );
      }
      if (marginRight < 0 || marginRight > 10) {
        throw new PageSetupValidationError(
          `Invalid right margin: ${marginRight} inches (must be 0-10)`,
          'margins.right',
          marginRight
        );
      }
      if (marginBottom < 0 || marginBottom > 10) {
        throw new PageSetupValidationError(
          `Invalid bottom margin: ${marginBottom} inches (must be 0-10)`,
          'margins.bottom',
          marginBottom
        );
      }
      if (marginLeft < 0 || marginLeft > 10) {
        throw new PageSetupValidationError(
          `Invalid left margin: ${marginLeft} inches (must be 0-10)`,
          'margins.left',
          marginLeft
        );
      }

      // Step 1: Convert inches to CSS pixels (96 DPI standard)
      const widthPx = widthInches * 96;
      const heightPx = heightInches * 96;
      const marginTopPx = marginTop * 96;
      const marginRightPx = marginRight * 96;
      const marginBottomPx = marginBottom * 96;
      const marginLeftPx = marginLeft * 96;

      // Step 2: Calculate content rectangle (page minus margins)
      const contentX = marginLeftPx;
      const contentY = marginTopPx;
      const contentW = widthPx - marginLeftPx - marginRightPx;
      const contentH = heightPx - marginTopPx - marginBottomPx;

      // Validate content area is positive
      if (contentW <= 0) {
        throw new PageSetupValidationError(
          `Content width is ${contentW.toFixed(2)}px (negative or zero). Margins too large for paper width.`,
          'margins',
          { left: marginLeft, right: marginRight, pageWidth: widthInches }
        );
      }
      if (contentH <= 0) {
        throw new PageSetupValidationError(
          `Content height is ${contentH.toFixed(2)}px (negative or zero). Margins too large for paper height.`,
          'margins',
          { top: marginTop, bottom: marginBottom, pageHeight: heightInches }
        );
      }

      // Step 3: Convert CSS pixels to 26.6 fixed-point
      const marginTopFp = FixedPoint.fromPx(marginTopPx);
      const marginRightFp = FixedPoint.fromPx(marginRightPx);
      const marginBottomFp = FixedPoint.fromPx(marginBottomPx);
      const marginLeftFp = FixedPoint.fromPx(marginLeftPx);
      const contentXFp = FixedPoint.fromPx(contentX);
      const contentYFp = FixedPoint.fromPx(contentY);
      const contentWFp = FixedPoint.fromPx(contentW);
      const contentHFp = FixedPoint.fromPx(contentH);

      // Step 4: Extract page size name (remove dimensions from string)
      const pageSizeName = dbPageSetup.paperSize?.name?.split('(')[0].trim() || "Letter";

      // Step 5: Build WASM PageSetup
      const wasmPageSetup: PageSetup = {
        page_size: pageSizeName,
        margins_fp: {
          top: marginTopFp,
          right: marginRightFp,
          bottom: marginBottomFp,
          left: marginLeftFp
        },
        columns: 1, // Single column layout (multi-column not yet supported)
        orientation: dbPageSetup.orientation || 'portrait',
        content_rect_fp: {
          x_fp: contentXFp,
          y_fp: contentYFp,
          w_fp: contentWFp,
          h_fp: contentHFp
        }
      };

      // Log conversion for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log('[PageSetupConverter] Database → WASM conversion:', {
          input: {
            paperSize: `${widthInches}" × ${heightInches}"`,
            margins: `T:${marginTop}" R:${marginRight}" B:${marginBottom}" L:${marginLeft}"`
          },
          output: {
            page_size: pageSizeName,
            margins_fp: wasmPageSetup.margins_fp,
            content_rect_fp: wasmPageSetup.content_rect_fp
          },
          calculations: {
            pagePixels: `${widthPx}px × ${heightPx}px`,
            contentPixels: `${contentW.toFixed(1)}px × ${contentH.toFixed(1)}px`,
            contentInches: `${(contentW / 96).toFixed(2)}" × ${(contentH / 96).toFixed(2)}"`
          }
        });
      }

      return wasmPageSetup;

    } catch (error) {
      if (error instanceof PageSetupValidationError) {
        console.error('[PageSetupConverter] Validation failed:', error.message, {
          field: error.field,
          value: error.value
        });
        throw error;
      }
      
      console.error('[PageSetupConverter] Unexpected error during conversion:', error);
      console.warn('[PageSetupConverter] Falling back to default Letter page setup');
      return PAGE_SIZES.LETTER;
    }
  }

  /**
   * Convert WASM PageSetup (fixed-point) to database page setup (inches)
   * 
   * Used for displaying page setup in UI or saving back to database
   * 
   * @param wasmPageSetup - WASM PageSetup with 26.6 fixed-point coordinates
   * @returns Database-compatible page setup with inches
   * 
   * @example
   * const wasmSetup = PAGE_SIZES.LETTER;
   * const dbSetup = PageSetupConverter.fromWasmFormat(wasmSetup);
   * // Result: { paperSize: { width: 8.5, height: 11 }, margins: { top: 0.75, ... } }
   */
  static fromWasmFormat(wasmPageSetup: PageSetup): DatabasePageSetup {
    try {
      // Step 1: Convert fixed-point margins to CSS pixels
      const marginTopPx = FixedPoint.toPx(wasmPageSetup.margins_fp.top);
      const marginRightPx = FixedPoint.toPx(wasmPageSetup.margins_fp.right);
      const marginBottomPx = FixedPoint.toPx(wasmPageSetup.margins_fp.bottom);
      const marginLeftPx = FixedPoint.toPx(wasmPageSetup.margins_fp.left);

      // Step 2: Convert fixed-point content rectangle to CSS pixels
      const contentWPx = FixedPoint.toPx(wasmPageSetup.content_rect_fp.w_fp);
      const contentHPx = FixedPoint.toPx(wasmPageSetup.content_rect_fp.h_fp);

      // Step 3: Calculate page dimensions from content + margins
      const widthPx = contentWPx + marginLeftPx + marginRightPx;
      const heightPx = contentHPx + marginTopPx + marginBottomPx;

      // Step 4: Convert CSS pixels to inches
      const widthInches = widthPx / 96;
      const heightInches = heightPx / 96;
      const marginTopInches = marginTopPx / 96;
      const marginRightInches = marginRightPx / 96;
      const marginBottomInches = marginBottomPx / 96;
      const marginLeftInches = marginLeftPx / 96;

      // Step 5: Format page size name
      const pageSizeName = typeof wasmPageSetup.page_size === 'string'
        ? `${wasmPageSetup.page_size} (${widthInches.toFixed(2)}" x ${heightInches.toFixed(2)}")`
        : `Custom (${widthInches.toFixed(2)}" x ${heightInches.toFixed(2)}")`;

      // Step 6: Build database page setup
      const dbPageSetup: DatabasePageSetup = {
        paperSize: {
          name: pageSizeName,
          width: parseFloat(widthInches.toFixed(2)),
          height: parseFloat(heightInches.toFixed(2))
        },
        orientation: wasmPageSetup.orientation,
        margins: {
          top: parseFloat(marginTopInches.toFixed(2)),
          right: parseFloat(marginRightInches.toFixed(2)),
          bottom: parseFloat(marginBottomInches.toFixed(2)),
          left: parseFloat(marginLeftInches.toFixed(2))
        }
      };

      return dbPageSetup;

    } catch (error) {
      console.error('[PageSetupConverter] Error converting WASM → Database format:', error);
      throw error;
    }
  }

  /**
   * Validate database page setup structure
   * 
   * @param pageSetup - Page setup to validate
   * @returns true if valid, false otherwise
   */
  static isValid(pageSetup: any): pageSetup is DatabasePageSetup {
    if (!pageSetup || typeof pageSetup !== 'object') {
      return false;
    }

    // Check paperSize
    if (!pageSetup.paperSize || typeof pageSetup.paperSize !== 'object') {
      return false;
    }
    if (typeof pageSetup.paperSize.width !== 'number' || pageSetup.paperSize.width <= 0) {
      return false;
    }
    if (typeof pageSetup.paperSize.height !== 'number' || pageSetup.paperSize.height <= 0) {
      return false;
    }

    // Check margins
    if (!pageSetup.margins || typeof pageSetup.margins !== 'object') {
      return false;
    }
    const margins = ['top', 'right', 'bottom', 'left'];
    for (const margin of margins) {
      if (typeof pageSetup.margins[margin] !== 'number' || pageSetup.margins[margin] < 0) {
        return false;
      }
    }

    // Check orientation
    if (pageSetup.orientation !== 'portrait' && pageSetup.orientation !== 'landscape') {
      return false;
    }

    return true;
  }

  /**
   * Get default page setup (US Letter, 0.75" margins)
   * 
   * @returns Default database page setup
   */
  static getDefault(): DatabasePageSetup {
    return {
      paperSize: {
        name: "Letter (8.5\" x 11\")",
        width: 8.5,
        height: 11
      },
      orientation: 'portrait',
      margins: {
        top: 0.75,
        right: 0.75,
        bottom: 0.75,
        left: 0.75
      },
      pageColor: '#ffffff'
    };
  }

  /**
   * Convert PageSetup to InitializeDocument format
   *
   * Converts WASM PageSetup (fixed-point) to the flat structure needed by initializeDocument.
   * This replaces the worker-specific convertPageSetupFromInternal function.
   *
   * @param pageSetup - WASM PageSetup with fixed-point coordinates
   * @returns InitializeDocumentPageSetup for worker initialization
   */
  static toInitializeDocumentFormat(pageSetup: PageSetup): InitializeDocumentPageSetup {
    // Calculate page dimensions in inches from content rect + margins
    // Page width = content width + left margin + right margin (all in fixed-point)
    const totalWidthFp = pageSetup.content_rect_fp.w_fp + pageSetup.margins_fp.left + pageSetup.margins_fp.right;
    const totalHeightFp = pageSetup.content_rect_fp.h_fp + pageSetup.margins_fp.top + pageSetup.margins_fp.bottom;

    // Convert to inches (fixed-point → pixels → inches)
    const pageWidthInches = totalWidthFp / 64 / 96;
    const pageHeightInches = totalHeightFp / 64 / 96;

    return {
      pageSize: { width: pageWidthInches, height: pageHeightInches },
      orientation: pageSetup.orientation,
      marginsTop: this.fixedPointToInches(pageSetup.margins_fp.top),
      marginsRight: this.fixedPointToInches(pageSetup.margins_fp.right),
      marginsBottom: this.fixedPointToInches(pageSetup.margins_fp.bottom),
      marginsLeft: this.fixedPointToInches(pageSetup.margins_fp.left),
      columns: pageSetup.columns,
      contentRectXFp: pageSetup.content_rect_fp.x_fp,
      contentRectYFp: pageSetup.content_rect_fp.y_fp,
      contentRectWFp: pageSetup.content_rect_fp.w_fp,
      contentRectHFp: pageSetup.content_rect_fp.h_fp
    };
  }

  /**
   * Convert InitializeDocument format to PageSetup
   *
   * Converts flat initializeDocument format to WASM PageSetup (fixed-point).
   * This replaces the worker-specific convertPageSetupToInternal function.
   *
   * @param initFormat - InitializeDocumentPageSetup from worker options
   * @returns WASM PageSetup with fixed-point coordinates
   */
  static fromInitializeDocumentFormat(initFormat: InitializeDocumentPageSetup): PageSetup {
    // Convert page size to string format
    const pageSizeStr = this.pageSizeToString(initFormat.pageSize);

    return {
      page_size: pageSizeStr,
      margins_fp: {
        top: this.inchesToFixedPoint(initFormat.marginsTop),
        right: this.inchesToFixedPoint(initFormat.marginsRight),
        bottom: this.inchesToFixedPoint(initFormat.marginsBottom),
        left: this.inchesToFixedPoint(initFormat.marginsLeft)
      },
      columns: initFormat.columns,
      orientation: initFormat.orientation,
      content_rect_fp: {
        x_fp: initFormat.contentRectXFp,
        y_fp: initFormat.contentRectYFp,
        w_fp: initFormat.contentRectWFp,
        h_fp: initFormat.contentRectHFp
      }
    };
  }

  /**
   * Convert inches to fixed-point units (26.6 format)
   * Formula: inches × 96 × 64 = inches × 6144
   *
   * @param inches - Value in inches
   * @returns Value in 26.6 fixed-point units
   */
  static inchesToFixedPoint(inches: number): number {
    return Math.round((inches || 0) * 96 * 64);
  }

  /**
   * Convert fixed-point units to inches
   * Formula: fixedPoint ÷ 64 ÷ 96
   *
   * @param fixedPoint - Value in 26.6 fixed-point units
   * @returns Value in inches
   */
  static fixedPointToInches(fixedPoint: number): number {
    return (fixedPoint || 0) / 64.0 / 96.0;
  }

  /**
   * Convert page size to string format
   * Handles both object {width, height} and string formats
   *
   * @param pageSize - Page size as object or string
   * @returns Page size as string (e.g., "8.5x11" or "Letter")
   */
  static pageSizeToString(pageSize: { width: number; height: number } | string): string {
    if (typeof pageSize === 'string') {
      return pageSize;
    }

    if (pageSize?.width && pageSize?.height) {
      return `${pageSize.width}x${pageSize.height}`;
    }

    // Default fallback
    return 'Letter';
  }
}

/**
 * Helper function for quick conversion (backward compatibility)
 *
 * @deprecated Use PageSetupConverter.toWasmFormat() instead
 */
export function convertPageSetupToWasm(dbPageSetup: Partial<DatabasePageSetup> | null | undefined): PageSetup {
  return PageSetupConverter.toWasmFormat(dbPageSetup);
}

/**
 * Helper function for quick reverse conversion (backward compatibility)
 *
 * @deprecated Use PageSetupConverter.fromWasmFormat() instead
 */
export function convertPageSetupFromWasm(wasmPageSetup: PageSetup): DatabasePageSetup {
  return PageSetupConverter.fromWasmFormat(wasmPageSetup);
}

